import Phaser from "phaser";
import type { PlatformAdapter } from "../../adapters/PlatformAdapter";
import type { Difficulty } from "../systems/WaveManager";

const DIFFICULTY_KEY = "td_difficulty";

// ─── Splash-screen palette — saturated, cinematic, mythic ──────────
const C_BG_TOP       = 0x0a0418;     // deep void
const C_BG_MID       = 0x1a0838;     // royal purple
const C_BG_LOW       = 0x2a1058;     // arcane violet
const C_AURORA_1     = 0x6a2aff;     // magenta-violet aurora
const C_AURORA_2     = 0x2a8aff;     // electric blue aurora
const C_AURORA_3     = 0xff4a8a;     // pink edge
const C_NEAR_BLACK   = 0x05030c;
const C_WHITE        = 0xffffff;
const C_WHITE_HOT    = 0xfff8c8;

// Element accents (saturated, glowing).
const C_FIRE         = 0xff5a3a;
const C_FIRE_HOT     = 0xffd060;
const C_FIRE_CORE    = 0xfff0c0;
const C_WATER        = 0x4cc9f0;
const C_WATER_HI     = 0xbef0ff;
const C_WATER_CORE   = 0xe8fbff;
const C_EARTH        = 0xd6a04a;
const C_EARTH_HI     = 0xffe0a0;
const C_EARTH_CORE   = 0xfff5d0;
const C_NATURE       = 0x7be36b;
const C_NATURE_HI    = 0xc8f0a0;
const C_NATURE_CORE  = 0xeafcd0;

// PLAY button — electric orange-gold burning core.
const C_GO_DEEP      = 0x5a1010;
const C_GO_DARK      = 0xa01820;
const C_GO           = 0xff3848;
const C_GO_HOT       = 0xff8048;
const C_GO_BRIGHT    = 0xffd060;
const C_GO_CORE      = 0xfff8d0;
const C_GO_RIM       = 0xfff0a0;

interface HeroDef {
  name: string;
  short: string;
  color: number;
  glow: number;
  core: number;
  letter: string;
}
const HEROES: HeroDef[] = [
  { name: "LINA",      short: "Slayer of the Sun",   color: C_FIRE,   glow: C_FIRE_HOT,   core: C_FIRE_CORE,   letter: "L" },
  { name: "RYLAI",     short: "Crystal Maiden",       color: C_WATER,  glow: C_WATER_HI,   core: C_WATER_CORE,  letter: "R" },
  { name: "ZHARVAKKO", short: "Witch Doctor",         color: C_EARTH,  glow: C_EARTH_HI,   core: C_EARTH_CORE,  letter: "Z" },
  { name: "MIRANA",    short: "Princess of the Moon", color: C_NATURE, glow: C_NATURE_HI,  core: C_NATURE_CORE, letter: "M" },
];

interface DiffEntry { key: Difficulty; label: string; tint: number; }
const DIFFS: DiffEntry[] = [
  { key: "easy",   label: "NOVICE", tint: C_NATURE },
  { key: "medium", label: "HERO",   tint: C_EARTH_HI },
  { key: "hard",   label: "LEGEND", tint: C_GO },
];

export class MenuScene extends Phaser.Scene {
  private diffButtons: Record<Difficulty, { container: Phaser.GameObjects.Container; bg: Phaser.GameObjects.Graphics; label: Phaser.GameObjects.Text; tint: number }> = {} as never;
  private currentDiff: Difficulty = "easy";

  constructor() { super({ key: "MenuScene" }); }

  create(): void {
    const adapter = this.registry.get("adapter") as PlatformAdapter;
    const player = adapter.getPlayer();
    try {
      const stored = localStorage.getItem(DIFFICULTY_KEY) as Difficulty | null;
      if (stored === "easy" || stored === "medium" || stored === "hard") this.currentDiff = stored;
    } catch { /* ignore */ }
    this.registry.set("difficulty", this.currentDiff);

    const { width, height } = this.scale;

    this.drawCosmicBackdrop(width, height);
    this.drawAuroraStreaks(width, height);
    this.drawStarsAndMotes(width, height);
    this.drawSpotlightBeams(width, height);
    this.drawCentralPrism(width, height);
    this.drawHeroLineup(width, height);
    this.drawTitle(width, height);
    this.drawTagline(width, height);
    this.drawWelcome(width, height, player.username);
    this.drawDifficultyPills(width, height, adapter);
    this.drawPlayButton(width, height, adapter);
    this.drawTapHint(width, height);
    this.drawFootnote(width, height, adapter.name);
  }

  // ───────────────────────────────────────────────────────────────────
  // Cosmic backdrop — deep void → royal purple → arcane violet gradient
  // with a subtle radial glow behind the central prism.
  // ───────────────────────────────────────────────────────────────────
  private drawCosmicBackdrop(w: number, h: number): void {
    this.cameras.main.setBackgroundColor(C_BG_TOP);
    const g = this.add.graphics();
    const bands = 60;
    for (let i = 0; i < bands; i++) {
      const t = i / (bands - 1);
      let from = C_BG_TOP, to = C_BG_MID, mix = t * 2;
      if (t > 0.5) { from = C_BG_MID; to = C_BG_LOW; mix = (t - 0.5) * 2; }
      const r = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.IntegerToColor(from),
        Phaser.Display.Color.IntegerToColor(to),
        100, Math.floor(mix * 100),
      );
      g.fillStyle(Phaser.Display.Color.GetColor(r.r, r.g, r.b), 1);
      g.fillRect(0, (h * i) / bands, w, h / bands + 1);
    }
    // Radial halo behind the central prism.
    for (let i = 0; i < 10; i++) {
      g.fillStyle(C_AURORA_1, 0.020 + (10 - i) * 0.008);
      g.fillCircle(w / 2, h * 0.42, 60 + i * 30);
    }
    for (let i = 0; i < 8; i++) {
      g.fillStyle(C_AURORA_3, 0.025 + (8 - i) * 0.006);
      g.fillCircle(w / 2, h * 0.42, 40 + i * 18);
    }
    // Bottom darkening for foreground contrast.
    g.fillStyle(C_NEAR_BLACK, 0.6);
    g.fillRect(0, h - 130, w, 130);
    // Top darkening for title contrast.
    g.fillStyle(C_NEAR_BLACK, 0.5);
    g.fillRect(0, 0, w, 50);
  }

  // ───────────────────────────────────────────────────────────────────
  // Slow drifting aurora streaks — diagonal magenta/blue ribbons
  // sweeping across the upper half. Pure mood.
  // ───────────────────────────────────────────────────────────────────
  private drawAuroraStreaks(w: number, h: number): void {
    const colors = [C_AURORA_1, C_AURORA_2, C_AURORA_3];
    for (let i = 0; i < 6; i++) {
      const y = h * (0.10 + i * 0.06);
      const color = colors[i % 3];
      const streak = this.add.ellipse(Math.random() * w, y, 460, 26, color, 0.18);
      streak.setRotation(-0.15 - Math.random() * 0.08);
      this.tweens.add({
        targets: streak,
        x: { from: -300, to: w + 300 },
        alpha: { from: 0.06, to: 0.22 },
        duration: 14000 + Math.random() * 6000,
        repeat: -1,
        delay: i * 2400,
        ease: "Linear",
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Distant stars (small twinklers) + element-colored magic motes
  // floating upward from below — give the page constant motion.
  // ───────────────────────────────────────────────────────────────────
  private drawStarsAndMotes(w: number, h: number): void {
    // Stars.
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h * 0.55;
      const r = 0.4 + Math.random() * 0.9;
      const star = this.add.circle(x, y, r, C_WHITE_HOT, 0.7);
      this.tweens.add({
        targets: star,
        alpha: { from: 0.2, to: 1 },
        duration: 1100 + Math.random() * 2200,
        yoyo: true, repeat: -1, delay: Math.random() * 3000, ease: "Sine.easeInOut",
      });
    }
    // Element motes drifting upward.
    const motePalette = [C_FIRE_HOT, C_WATER_HI, C_EARTH_HI, C_NATURE_HI, C_AURORA_3];
    for (let i = 0; i < 22; i++) {
      const x = 30 + Math.random() * (w - 60);
      const y = h - Math.random() * h * 0.6;
      const color = motePalette[Math.floor(Math.random() * motePalette.length)];
      const r = 0.7 + Math.random() * 1.1;
      const mote = this.add.circle(x, y, r, color, 0.85);
      this.tweens.add({
        targets: mote,
        y: { from: y, to: y - 260 - Math.random() * 220 },
        x: { from: x, to: x + (Math.random() - 0.5) * 80 },
        alpha: { from: 0.95, to: 0 },
        duration: 6500 + Math.random() * 4500,
        repeat: -1,
        delay: Math.random() * 5000,
        ease: "Sine.easeOut",
        onRepeat: () => { mote.x = 30 + Math.random() * (w - 60); },
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Stage spotlight beams — 3 wedge-shaped light cones angling down
  // from above the central prism, like a concert stage reveal.
  // ───────────────────────────────────────────────────────────────────
  private drawSpotlightBeams(w: number, h: number): void {
    const cx = w / 2;
    const top = -20;
    const focus = h * 0.42;
    const colors = [C_AURORA_2, C_AURORA_1, C_AURORA_3];
    for (let i = 0; i < 3; i++) {
      const offset = (i - 1) * 70;
      const angle = (offset / focus);
      const beam = this.add.graphics();
      beam.setDepth(-1);
      beam.fillStyle(colors[i], 0.10);
      beam.fillPoints([
        { x: cx + offset - 18, y: top },
        { x: cx + offset + 18, y: top },
        { x: cx + 80 * Math.sin(angle), y: focus + 20 },
        { x: cx - 80 * Math.sin(angle), y: focus + 20 },
      ], true);
      beam.fillStyle(colors[i], 0.18);
      beam.fillPoints([
        { x: cx + offset - 8, y: top },
        { x: cx + offset + 8, y: top },
        { x: cx + 30 * Math.sin(angle), y: focus + 10 },
        { x: cx - 30 * Math.sin(angle), y: focus + 10 },
      ], true);
      this.tweens.add({
        targets: beam,
        alpha: { from: 0.7, to: 1.0 },
        duration: 1800 + i * 400,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // CENTRAL ELEMENTAL PRISM — the centerpiece. A big rotating crystal
  // with 4 orbiting element orbs and a swirling rune ring. THIS is the
  // image that makes the player click PLAY.
  // ───────────────────────────────────────────────────────────────────
  private drawCentralPrism(w: number, h: number): void {
    const cx = w / 2;
    const cy = h * 0.42;
    const container = this.add.container(cx, cy);
    container.setDepth(5);

    // Outer aura — huge radial glow.
    const auraFar = this.add.circle(0, 0, 120, C_AURORA_1, 0.18);
    const auraMid = this.add.circle(0, 0, 90, C_AURORA_3, 0.25);
    const auraNear = this.add.circle(0, 0, 60, C_WHITE_HOT, 0.45);
    container.add([auraFar, auraMid, auraNear]);
    this.tweens.add({
      targets: auraFar, scale: { from: 0.92, to: 1.15 }, alpha: { from: 0.12, to: 0.30 },
      duration: 1800, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: auraMid, scale: { from: 0.95, to: 1.20 }, alpha: { from: 0.18, to: 0.42 },
      duration: 1400, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: auraNear, scale: { from: 0.9, to: 1.1 }, alpha: { from: 0.35, to: 0.70 },
      duration: 900, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });

    // Spinning rune ring — bright cyan ring with 12 rune ticks.
    const ringContainer = this.add.container(0, 0);
    const ring = this.add.graphics();
    ring.lineStyle(2.4, C_AURORA_2, 0.85);
    ring.strokeCircle(0, 0, 64);
    ring.lineStyle(1.2, C_WATER_CORE, 0.85);
    ring.strokeCircle(0, 0, 64);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const x1 = Math.cos(a) * 60;
      const y1 = Math.sin(a) * 60;
      const x2 = Math.cos(a) * 68;
      const y2 = Math.sin(a) * 68;
      ring.lineStyle(1.6, C_AURORA_2, 0.95);
      ring.beginPath(); ring.moveTo(x1, y1); ring.lineTo(x2, y2); ring.strokePath();
      // Rune diamond at tip.
      ring.fillStyle(C_WATER_HI, 1);
      ring.fillTriangle(x2, y2 - 2.2, x2 + 2, y2, x2 - 2, y2);
      ring.fillTriangle(x2, y2 + 2.2, x2 + 2, y2, x2 - 2, y2);
    }
    ringContainer.add(ring);
    container.add(ringContainer);
    this.tweens.add({
      targets: ringContainer, angle: 360,
      duration: 18000, repeat: -1, ease: "Linear",
    });

    // Counter-rotating inner rune ring.
    const ringInnerContainer = this.add.container(0, 0);
    const ringInner = this.add.graphics();
    ringInner.lineStyle(1.4, C_AURORA_3, 0.75);
    ringInner.strokeCircle(0, 0, 50);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const x = Math.cos(a) * 50;
      const y = Math.sin(a) * 50;
      ringInner.fillStyle(C_AURORA_3, 1);
      ringInner.fillCircle(x, y, 2);
      ringInner.fillStyle(C_WHITE_HOT, 1);
      ringInner.fillCircle(x - 0.4, y - 0.4, 0.7);
    }
    ringInnerContainer.add(ringInner);
    container.add(ringInnerContainer);
    this.tweens.add({
      targets: ringInnerContainer, angle: -360,
      duration: 11000, repeat: -1, ease: "Linear",
    });

    // The central crystal — diamond shape with 4-element faceted faces.
    const crystal = this.add.graphics();
    const D = 36;     // crystal radius
    // Glow under crystal.
    crystal.fillStyle(C_WHITE_HOT, 0.5);
    crystal.fillCircle(0, 0, 24);
    // Bottom shadow facet.
    crystal.fillStyle(C_NEAR_BLACK, 0.85);
    crystal.fillTriangle(0, D, -D * 0.7, 0, D * 0.7, 0);
    // Four element-colored top facets.
    crystal.fillStyle(C_FIRE, 1);
    crystal.fillTriangle(0, -D, -D * 0.7, 0, 0, 0);
    crystal.fillStyle(C_WATER, 1);
    crystal.fillTriangle(0, -D, D * 0.7, 0, 0, 0);
    crystal.fillStyle(C_NATURE, 1);
    crystal.fillTriangle(0, 0, -D * 0.7, 0, 0, D * 0.6);
    crystal.fillStyle(C_EARTH, 1);
    crystal.fillTriangle(0, 0, D * 0.7, 0, 0, D * 0.6);
    // Hot center seam.
    crystal.fillStyle(C_WHITE_HOT, 0.95);
    crystal.fillTriangle(0, -D, -3, 0, 3, 0);
    crystal.fillTriangle(0, 0, -3, 0, 0, D * 0.6);
    crystal.fillTriangle(0, -D, -2, -D * 0.5, 2, -D * 0.5);
    // Edge highlights.
    crystal.lineStyle(1.2, C_WHITE, 0.85);
    crystal.beginPath();
    crystal.moveTo(0, -D); crystal.lineTo(-D * 0.7, 0);
    crystal.lineTo(0, D); crystal.lineTo(D * 0.7, 0); crystal.closePath();
    crystal.strokePath();
    crystal.lineStyle(0.6, C_WHITE_HOT, 1);
    crystal.beginPath();
    crystal.moveTo(0, -D + 2); crystal.lineTo(-D * 0.7 + 1.5, -1);
    crystal.strokePath();
    container.add(crystal);

    // Subtle crystal scale pulse + slight wobble.
    this.tweens.add({
      targets: crystal,
      scale: { from: 0.95, to: 1.08 },
      duration: 1100, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: container,
      angle: { from: -2, to: 2 },
      duration: 3800, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });

    // 4 ORBITING ELEMENT ORBS — fire/water/earth/nature each on an ellipse.
    const orbR = 9;
    const orbitRX = 92;
    const orbitRY = 28;
    const elements = [
      { color: C_FIRE,   glow: C_FIRE_HOT,   core: C_FIRE_CORE },
      { color: C_WATER,  glow: C_WATER_HI,   core: C_WATER_CORE },
      { color: C_EARTH,  glow: C_EARTH_HI,   core: C_EARTH_CORE },
      { color: C_NATURE, glow: C_NATURE_HI,  core: C_NATURE_CORE },
    ];
    const orbContainers: Phaser.GameObjects.Container[] = [];
    for (let i = 0; i < 4; i++) {
      const orb = this.add.container(0, 0);
      const halo = this.add.circle(0, 0, orbR + 6, elements[i].glow, 0.35);
      const body = this.add.circle(0, 0, orbR, elements[i].color, 1);
      body.setStrokeStyle(1.2, C_WHITE, 0.95);
      const core = this.add.circle(0, 0, orbR * 0.55, elements[i].core, 0.95);
      const spec = this.add.circle(-2, -2, orbR * 0.30, C_WHITE_HOT, 0.95);
      orb.add([halo, body, core, spec]);
      this.tweens.add({
        targets: halo,
        scale: { from: 0.9, to: 1.4 },
        alpha: { from: 0.2, to: 0.6 },
        duration: 900 + i * 80, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
      this.tweens.add({
        targets: core,
        alpha: { from: 0.7, to: 1 }, scale: { from: 0.9, to: 1.2 },
        duration: 700 + i * 60, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
      container.add(orb);
      orbContainers.push(orb);
    }
    const orbit = { t: 0 };
    this.tweens.add({
      targets: orbit, t: Math.PI * 2,
      duration: 11000, repeat: -1, ease: "Linear",
      onUpdate: () => {
        for (let i = 0; i < orbContainers.length; i++) {
          const a = orbit.t + (i / orbContainers.length) * Math.PI * 2;
          orbContainers[i].setPosition(Math.cos(a) * orbitRX, Math.sin(a) * orbitRY);
          const depth = (Math.sin(a) + 1) * 0.5;       // 0 back, 1 front
          orbContainers[i].setScale(0.78 + depth * 0.50);
          orbContainers[i].setAlpha(0.55 + depth * 0.45);
          orbContainers[i].setDepth(depth > 0.5 ? 10 : -2);
        }
      },
    });

    // Energy streamers from crystal corners — flickering lines.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const len = 75 + Math.random() * 20;
      const x = Math.cos(a) * len;
      const y = Math.sin(a) * len;
      const streamer = this.add.line(0, 0, 0, 0, x, y, [C_AURORA_2, C_AURORA_3, C_FIRE_HOT, C_NATURE_HI, C_WATER_HI, C_EARTH_HI][i], 0.5);
      streamer.setLineWidth(1.2);
      streamer.setOrigin(0, 0);
      container.add(streamer);
      this.tweens.add({
        targets: streamer,
        alpha: { from: 0.0, to: 0.85 },
        duration: 700 + i * 120,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Hero lineup — 4 large silhouette heroes posed dramatically across
  // the bottom of the stage area, each rim-lit by their element. They
  // are the team you'll deploy.
  // ───────────────────────────────────────────────────────────────────
  private drawHeroLineup(w: number, h: number): void {
    const cy = h * 0.58;
    const count = HEROES.length;
    const slot = Math.min(85, (w - 30) / count);
    const totalW = slot * count;
    const x0 = w / 2 - totalW / 2 + slot / 2;

    for (let i = 0; i < count; i++) {
      const hero = HEROES[i];
      const cx = x0 + i * slot;
      // Subtle ground pool of element light.
      const pool = this.add.ellipse(cx, cy + 38, slot * 0.85, slot * 0.18, hero.glow, 0.35);
      this.tweens.add({
        targets: pool,
        alpha: { from: 0.20, to: 0.55 },
        scaleX: { from: 0.92, to: 1.10 },
        duration: 1300 + i * 200,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });

      const heroC = this.add.container(cx, cy);
      heroC.setDepth(8);
      // Strong rim-light backglow.
      const back = this.add.circle(0, 0, 36, hero.glow, 0.40);
      this.tweens.add({
        targets: back, scale: { from: 0.92, to: 1.18 }, alpha: { from: 0.30, to: 0.65 },
        duration: 1100 + i * 80, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
      heroC.add(back);

      // Hero silhouette body — head + shoulders + torso (filled).
      const body = this.add.graphics();
      // Shadow under.
      body.fillStyle(C_NEAR_BLACK, 0.85);
      body.fillEllipse(0, 28, 50, 16);
      // Torso / shoulders.
      body.fillStyle(hero.color, 1);
      body.fillEllipse(0, 20, 44, 36);
      // Cape / aura behind shoulders.
      body.fillStyle(hero.glow, 0.7);
      body.fillTriangle(-22, 10, -32, 36, -10, 18);
      body.fillTriangle(22, 10, 32, 36, 10, 18);
      // Head.
      body.fillStyle(hero.color, 1);
      body.fillCircle(0, -6, 14);
      // Helmet/crown stripe across forehead.
      body.fillStyle(hero.glow, 1);
      body.fillRect(-12, -10, 24, 3);
      // Bright eye glow.
      body.fillStyle(hero.core, 1);
      body.fillCircle(-4, -5, 1.6);
      body.fillCircle(4, -5, 1.6);
      // Element sigil on chest.
      body.fillStyle(hero.core, 1);
      body.fillTriangle(0, 12, -4, 20, 4, 20);
      body.fillTriangle(0, 28, -4, 20, 4, 20);
      // Rim highlight on left side of head.
      body.fillStyle(C_WHITE_HOT, 0.55);
      body.fillEllipse(-6, -8, 6, 8);
      body.fillStyle(C_WHITE_HOT, 0.45);
      body.fillEllipse(-14, 16, 4, 14);
      heroC.add(body);

      // Floating element orb above hero's head — their weapon/spell.
      const orb = this.add.circle(0, -28, 5, hero.color, 1);
      orb.setStrokeStyle(1, C_WHITE, 0.95);
      const orbCore = this.add.circle(0, -28, 2.5, hero.core, 1);
      heroC.add([orb, orbCore]);
      this.tweens.add({
        targets: [orb, orbCore],
        y: { from: -28, to: -32 },
        duration: 1200 + i * 110,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
      this.tweens.add({
        targets: orbCore,
        alpha: { from: 0.6, to: 1 }, scale: { from: 0.8, to: 1.4 },
        duration: 700, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });

      // Hero name tag — bold gradient text.
      this.add
        .text(cx, cy + 50, hero.name, {
          fontFamily: '"Cinzel", "Trajan Pro", Georgia, serif',
          fontSize: "11px",
          fontStyle: "bold",
          color: "#ffffff",
          stroke: "#0a0418",
          strokeThickness: 3,
          letterSpacing: 1,
        } as Phaser.Types.GameObjects.Text.TextStyle)
        .setOrigin(0.5)
        .setDepth(9);

      // Small breathing animation on the whole hero.
      this.tweens.add({
        targets: heroC,
        y: { from: cy, to: cy - 4 },
        duration: 1800 + i * 180,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // GIANT TITLE — "BATTLE ELEMENT" — big, white-hot, with element-color
  // glow drop shadow + cinematic punch.
  // ───────────────────────────────────────────────────────────────────
  private drawTitle(w: number, h: number): void {
    const cy = h * 0.12;

    // Backlight burst — a bright halo behind the title.
    const halo = this.add.ellipse(w / 2, cy + 4, 360, 84, C_AURORA_3, 0.22);
    this.tweens.add({
      targets: halo,
      scaleX: { from: 0.95, to: 1.15 },
      alpha: { from: 0.15, to: 0.40 },
      duration: 1800, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });

    // Element-color drop shadows for "BATTLE ELEMENT" — stack 4 colored
    // copies behind the white main title to make it pop chromatically.
    const shadowOffsets: Array<[number, number, string]> = [
      [-3,  2, "#ff5a3a"],     // fire
      [ 3,  2, "#4cc9f0"],     // water
      [ 0,  4, "#d6a04a"],     // earth
      [ 0, -3, "#7be36b"],     // nature
    ];
    for (const [dx, dy, color] of shadowOffsets) {
      this.add
        .text(w / 2 + dx, cy + dy, "BATTLE  ELEMENT", {
          fontFamily: '"Cinzel", "Trajan Pro", Georgia, serif',
          fontSize: "36px",
          fontStyle: "bold",
          color,
          stroke: "#0a0418",
          strokeThickness: 1,
          letterSpacing: 3,
        } as Phaser.Types.GameObjects.Text.TextStyle)
        .setOrigin(0.5)
        .setAlpha(0.55);
    }

    // Main white-hot title.
    const title = this.add
      .text(w / 2, cy, "BATTLE  ELEMENT", {
        fontFamily: '"Cinzel", "Trajan Pro", Georgia, serif',
        fontSize: "36px",
        fontStyle: "bold",
        color: "#ffffff",
        stroke: "#1a0838",
        strokeThickness: 5,
        letterSpacing: 3,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
    title.setShadow(0, 0, "#fff8c8", 14, true, true);

    // Subtle scale punch on load.
    title.setScale(0.6);
    title.setAlpha(0);
    this.tweens.add({
      targets: title,
      scale: 1,
      alpha: 1,
      duration: 600,
      ease: "Back.easeOut",
    });
    // Continuous shimmer.
    this.tweens.add({
      targets: title,
      alpha: { from: 0.92, to: 1 },
      duration: 1400, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      delay: 700,
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // Tagline under the title — punchy 3-word battle cry.
  // ───────────────────────────────────────────────────────────────────
  private drawTagline(w: number, h: number): void {
    const cy = h * 0.165;
    const tag = this.add
      .text(w / 2, cy, "DEFEND  ·  EVOLVE  ·  CONQUER", {
        fontFamily: '"Cinzel", Georgia, serif',
        fontSize: "11px",
        fontStyle: "bold",
        color: "#fdd87a",
        stroke: "#0a0418",
        strokeThickness: 3,
        letterSpacing: 6,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
    tag.setShadow(0, 0, "#ff8048", 8, true, false);
    tag.setAlpha(0);
    this.tweens.add({
      targets: tag,
      alpha: 1,
      duration: 600,
      delay: 500,
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // Welcome (small, glows).
  // ───────────────────────────────────────────────────────────────────
  private drawWelcome(w: number, h: number, username: string): void {
    this.add
      .text(w / 2, h * 0.195, `WELCOME,  ${username.toUpperCase()}`, {
        fontFamily: '"Cinzel", Georgia, serif',
        fontSize: "9px",
        fontStyle: "italic",
        color: "#bef0ff",
        stroke: "#0a0418",
        strokeThickness: 2,
        letterSpacing: 3,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
  }

  // ───────────────────────────────────────────────────────────────────
  // Difficulty pills — sleek capsule selectors above the PLAY button.
  // ───────────────────────────────────────────────────────────────────
  private drawDifficultyPills(w: number, h: number, adapter: PlatformAdapter): void {
    const cy = h * 0.715;

    this.add
      .text(w / 2, cy - 22, "DIFFICULTY", {
        fontFamily: '"Cinzel", Georgia, serif',
        fontSize: "9px",
        fontStyle: "bold",
        color: "#bef0ff",
        stroke: "#0a0418",
        strokeThickness: 2,
        letterSpacing: 4,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);

    const pillW = 96;
    const pillH = 26;
    const gap = 8;
    const totalW = pillW * 3 + gap * 2;
    const x0 = w / 2 - totalW / 2;

    DIFFS.forEach((d, i) => {
      const px = x0 + i * (pillW + gap);
      const py = cy - pillH / 2;
      const container = this.add.container(0, 0);
      const bg = this.add.graphics();
      this.paintPill(bg, px, py, pillW, pillH, d.tint, false);
      container.add(bg);
      const label = this.add
        .text(px + pillW / 2, py + pillH / 2, d.label, {
          fontFamily: '"Cinzel", Georgia, serif',
          fontSize: "12px",
          fontStyle: "bold",
          color: "#ffffff",
          stroke: "#0a0418",
          strokeThickness: 2,
          letterSpacing: 2,
        } as Phaser.Types.GameObjects.Text.TextStyle)
        .setOrigin(0.5);
      container.add(label);
      const hit = this.add
        .rectangle(px + pillW / 2, py + pillH / 2, pillW, pillH, 0xffffff, 0)
        .setInteractive({ useHandCursor: true });
      container.add(hit);
      hit.on("pointerdown", () => {
        adapter.haptic("light");
        this.currentDiff = d.key;
        try { localStorage.setItem(DIFFICULTY_KEY, d.key); } catch { /* ignore */ }
        this.registry.set("difficulty", d.key);
        this.refreshDiffButtons();
      });
      this.diffButtons[d.key] = { container, bg, label, tint: d.tint };
      container.setData("geom", { px, py, pillW, pillH });
    });

    this.refreshDiffButtons();
  }

  private refreshDiffButtons(): void {
    (Object.keys(this.diffButtons) as Difficulty[]).forEach((k) => {
      const entry = this.diffButtons[k];
      const geom = entry.container.getData("geom") as { px: number; py: number; pillW: number; pillH: number };
      const isActive = k === this.currentDiff;
      entry.bg.clear();
      this.paintPill(entry.bg, geom.px, geom.py, geom.pillW, geom.pillH, entry.tint, isActive);
      entry.container.setAlpha(isActive ? 1 : 0.55);
      entry.label.setColor(isActive ? "#ffffff" : "#7a8aa0");
    });
  }

  private paintPill(
    g: Phaser.GameObjects.Graphics,
    x: number, y: number, w: number, h: number,
    tint: number, active: boolean,
  ): void {
    // Outer glow (active only).
    if (active) {
      g.fillStyle(tint, 0.32);
      g.fillRoundedRect(x - 4, y - 4, w + 8, h + 8, h / 2 + 4);
    }
    // Dark base.
    g.fillStyle(C_NEAR_BLACK, 0.95);
    g.fillRoundedRect(x, y, w, h, h / 2);
    // Inner fill — tint wash if active.
    if (active) {
      g.fillStyle(tint, 0.45);
      g.fillRoundedRect(x + 2, y + 2, w - 4, h - 4, (h - 4) / 2);
      g.fillStyle(C_WHITE_HOT, 0.30);
      g.fillRoundedRect(x + 3, y + 3, w - 6, (h - 6) * 0.45, (h - 6) / 2);
      g.lineStyle(1.4, tint, 1);
      g.strokeRoundedRect(x, y, w, h, h / 2);
      g.lineStyle(0.7, C_WHITE, 0.85);
      g.strokeRoundedRect(x + 2, y + 2, w - 4, h - 4, (h - 4) / 2);
    } else {
      g.fillStyle(0x1a0838, 1);
      g.fillRoundedRect(x + 2, y + 2, w - 4, h - 4, (h - 4) / 2);
      g.lineStyle(1, tint, 0.45);
      g.strokeRoundedRect(x, y, w, h, h / 2);
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // PLAY BUTTON — the centerpiece CTA. Huge fiery capsule with massive
  // pulsing halo. Designed to be impossible to ignore.
  // ───────────────────────────────────────────────────────────────────
  private drawPlayButton(w: number, h: number, adapter: PlatformAdapter): void {
    const cy = h * 0.815;
    const btnW = 248;
    const btnH = 72;

    // Multi-layer pulsing aura behind the button.
    const auraFar = this.add.ellipse(w / 2, cy, btnW + 100, btnH + 80, C_GO_HOT, 0.18);
    const auraMid = this.add.ellipse(w / 2, cy, btnW + 50, btnH + 40, C_GO_BRIGHT, 0.30);
    const auraNear = this.add.ellipse(w / 2, cy, btnW + 16, btnH + 14, C_GO_CORE, 0.55);
    this.tweens.add({
      targets: auraFar,
      scale: { from: 0.96, to: 1.12 },
      alpha: { from: 0.15, to: 0.36 },
      duration: 1400, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: auraMid,
      scale: { from: 0.97, to: 1.10 },
      alpha: { from: 0.25, to: 0.55 },
      duration: 1000, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: auraNear,
      alpha: { from: 0.45, to: 0.85 },
      duration: 700, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });

    const x0 = w / 2 - btnW / 2;
    const y0 = cy - btnH / 2;
    const container = this.add.container(0, 0);
    const frame = this.add.graphics();
    // Outer ring (gold rim).
    frame.fillStyle(C_GO_DEEP, 1);
    frame.fillRoundedRect(x0 - 6, y0 - 4, btnW + 12, btnH + 8, btnH / 2 + 4);
    frame.fillStyle(C_GO_BRIGHT, 1);
    frame.fillRoundedRect(x0 - 4, y0 - 2, btnW + 8, btnH + 4, btnH / 2 + 2);
    // Inner body — saturated fire gradient.
    frame.fillStyle(C_GO_DARK, 1);
    frame.fillRoundedRect(x0, y0, btnW, btnH, btnH / 2);
    frame.fillStyle(C_GO, 1);
    frame.fillRoundedRect(x0 + 3, y0 + 3, btnW - 6, btnH - 6, (btnH - 6) / 2);
    frame.fillStyle(C_GO_HOT, 1);
    frame.fillRoundedRect(x0 + 6, y0 + 6, btnW - 12, btnH - 12, (btnH - 12) / 2);
    // Top half lighter — glossy.
    frame.fillStyle(C_GO_BRIGHT, 1);
    frame.fillRoundedRect(x0 + 9, y0 + 9, btnW - 18, (btnH - 18) * 0.50, (btnH - 18) / 2);
    // Specular shine.
    frame.fillStyle(C_GO_CORE, 0.85);
    frame.fillRoundedRect(x0 + 14, y0 + 11, btnW - 28, 6, 3);
    // Gold inner rim line.
    frame.lineStyle(1.4, C_GO_RIM, 1);
    frame.strokeRoundedRect(x0 + 3, y0 + 3, btnW - 6, btnH - 6, (btnH - 6) / 2);
    container.add(frame);

    // Big bold PLAY text.
    const label = this.add
      .text(w / 2 + 18, cy + 1, "PLAY", {
        fontFamily: '"Cinzel", "Trajan Pro", Georgia, serif',
        fontSize: "34px",
        fontStyle: "bold",
        color: "#ffffff",
        stroke: "#5a1010",
        strokeThickness: 5,
        letterSpacing: 8,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
    label.setShadow(0, 2, "#000000", 4, true, true);
    container.add(label);

    // Play triangle to the left of the text.
    const tri = this.add.graphics();
    const triX = w / 2 - 70;
    tri.fillStyle(C_GO_DEEP, 1);
    tri.fillTriangle(triX - 10, cy - 14, triX - 10, cy + 14, triX + 12, cy);
    tri.fillStyle(C_GO_CORE, 1);
    tri.fillTriangle(triX - 7, cy - 11, triX - 7, cy + 11, triX + 8, cy);
    tri.fillStyle(C_WHITE, 1);
    tri.fillTriangle(triX - 5, cy - 7, triX - 5, cy + 7, triX + 3, cy);
    container.add(tri);

    // Sparkle particles around the button.
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2;
      const rX = btnW / 2 + 18;
      const rY = btnH / 2 + 14;
      const sx = w / 2 + Math.cos(ang) * rX;
      const sy = cy + Math.sin(ang) * rY;
      const spark = this.add.circle(sx, sy, 1.6, C_GO_RIM, 1);
      this.tweens.add({
        targets: spark,
        alpha: { from: 0.2, to: 1 },
        scale: { from: 0.6, to: 1.6 },
        duration: 600 + i * 90,
        yoyo: true, repeat: -1, ease: "Sine.easeInOut",
      });
    }

    const hit = this.add
      .rectangle(w / 2, cy, btnW + 30, btnH + 24, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hit);
    hit.on("pointerover", () => { this.tweens.add({ targets: container, scale: 1.05, duration: 120 }); });
    hit.on("pointerout",  () => { this.tweens.add({ targets: container, scale: 1, duration: 120 }); });
    hit.on("pointerdown", () => {
      adapter.haptic("light");
      this.registry.set("difficulty", this.currentDiff);
      this.cameras.main.flash(220, 255, 200, 80, true);
      this.time.delayedCall(150, () => this.scene.start("GameScene"));
    });

    // Continuous breathe + slight bob.
    this.tweens.add({
      targets: container,
      scale: { from: 1, to: 1.03 },
      duration: 950,
      yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: container,
      y: { from: 0, to: -3 },
      duration: 1600, yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // "TAP TO BEGIN" tiny pulse under the PLAY button.
  // ───────────────────────────────────────────────────────────────────
  private drawTapHint(w: number, h: number): void {
    const t = this.add
      .text(w / 2, h * 0.885, "⟶  TAP  TO  BEGIN  YOUR  LEGEND  ⟵", {
        fontFamily: '"Cinzel", Georgia, serif',
        fontSize: "9px",
        fontStyle: "italic",
        color: "#fdd87a",
        stroke: "#0a0418",
        strokeThickness: 2,
        letterSpacing: 3,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
    this.tweens.add({
      targets: t,
      alpha: { from: 0.45, to: 1 },
      duration: 900,
      yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // Footnote.
  // ───────────────────────────────────────────────────────────────────
  private drawFootnote(w: number, h: number, platform: string): void {
    this.add
      .text(w / 2, h - 18, `${platform.toUpperCase()}  ·  v0.1`, {
        fontFamily: '"Cinzel", Georgia, serif',
        fontSize: "8px",
        color: "#6a5aa0",
        letterSpacing: 3,
      } as Phaser.Types.GameObjects.Text.TextStyle)
      .setOrigin(0.5);
  }
}
